import { useEffect, useState } from 'react';
import { Calendar, Edit2, Save, Trash2, UserPlus, Users } from 'lucide-react';
import type { ComiteParticipant, ComiteReunionComplete } from '../../services/comiteService';
import { addParticipant, updateParticipant, deleteParticipant, updateReunion } from '../../services/comiteService';
import { getPartiesPrenantes, type PartiePrenante } from '../../services/partiesPrenantesService';
import { formatDateLocal } from '../../utils/dateFormatter';

type ToastFn = (message: string, type: 'success' | 'error') => void;

interface InfoParticipantsTabProps {
  reunion: ComiteReunionComplete;
  onUpdate: () => void;
  onDelete: () => void;
  showToast: ToastFn;
}

export default function InfoParticipantsTab({ reunion, onUpdate, onDelete, showToast }: InfoParticipantsTabProps) {
  const [isEditing, setIsEditing] = useState(false);
  const dateOnly = reunion.date_reunion ? reunion.date_reunion.split('T')[0] : '';
  const [formData, setFormData] = useState({
    type_rencontre: reunion.type_rencontre || 'Comité de Pilotage TI',
    date_reunion: dateOnly,
    heure_debut: reunion.heure_debut || '',
    lieu: reunion.lieu || '',
    duree_prevue_min: reunion.duree_prevue_min || 60,
    objectif_principal: reunion.objectif_principal || '',
    statut: reunion.statut || 'Planifiée',
  });

  const [showAdd, setShowAdd] = useState(false);
  const [partiesPrenantes, setPartiesPrenantes] = useState<PartiePrenante[]>([]);
  const [selectedPP, setSelectedPP] = useState<string>('');
  const [newParticipant, setNewParticipant] = useState<Partial<ComiteParticipant>>({
    nom: '',
    role: 'Membre',
    email: '',
    present: false,
  });

  useEffect(() => {
    if (!isEditing) {
      const d = reunion.date_reunion ? reunion.date_reunion.split('T')[0] : '';
      setFormData({
        type_rencontre: reunion.type_rencontre || 'Comité de Pilotage TI',
        date_reunion: d,
        heure_debut: reunion.heure_debut || '',
        lieu: reunion.lieu || '',
        duree_prevue_min: reunion.duree_prevue_min || 60,
        objectif_principal: reunion.objectif_principal || '',
        statut: reunion.statut || 'Planifiée',
      });
    }
  }, [reunion, isEditing]);

  useEffect(() => {
    (async () => {
      try {
        // Contacts disponibles = client de la réunion (+ BZ, selon la logique backend)
        const pp = await getPartiesPrenantes(reunion.client_id);
        setPartiesPrenantes(pp);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [reunion.client_id]);

  const handleSelectPP = (ppId: string) => {
    setSelectedPP(ppId);
    if (!ppId) {
      setNewParticipant({ nom: '', role: 'Membre', email: '', present: false });
      return;
    }
    const pp = partiesPrenantes.find((p) => String(p.id) === ppId);
    if (!pp) return;
    setNewParticipant({
      nom: `${pp.prenom} ${pp.nom}`,
      role: pp.role_principal || pp.titre_poste || 'Membre',
      email: pp.email || '',
      present: false,
    });
  };

  const saveInfo = async () => {
    try {
      if (!reunion.id) return;
      await updateReunion(reunion.id, formData as any);
      showToast('Réunion mise à jour!', 'success');
      setIsEditing(false);
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la mise à jour', 'error');
    }
  };

  const addOneParticipant = async () => {
    try {
      if (!reunion.id) return;
      if (!newParticipant.nom) return showToast('Le nom est requis', 'error');
      await addParticipant({ ...newParticipant, reunion_id: reunion.id });
      showToast('Participant ajouté!', 'success');
      setShowAdd(false);
      setSelectedPP('');
      setNewParticipant({ nom: '', role: 'Membre', email: '', present: false });
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast("Erreur lors de l'ajout", 'error');
    }
  };

  const togglePresence = async (participantId: number, currentStatus?: boolean) => {
    try {
      await updateParticipant(participantId, { present: !currentStatus });
      showToast(`Statut mis à jour: ${!currentStatus ? '✓ Présent' : 'Absent'}`, 'success');
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la mise à jour', 'error');
    }
  };

  const removeParticipant = async (participantId: number, nom: string) => {
    if (!confirm(`Retirer ${nom} de la réunion?`)) return;
    try {
      await deleteParticipant(participantId);
      showToast('Participant retiré', 'success');
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Calendar className="w-7 h-7 text-green-600" />
            Informations de la Réunion
          </h3>
          {!isEditing ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Modifier
              </button>
              <button
                onClick={onDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={saveInfo}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                Sauvegarder
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 rounded-xl p-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom de la réunion *</label>
              <input
                type="text"
                value={formData.type_rencontre}
                onChange={(e) => setFormData({ ...formData, type_rencontre: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
              <input
                type="date"
                value={formData.date_reunion}
                onChange={(e) => setFormData({ ...formData, date_reunion: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Heure</label>
              <input
                type="time"
                value={formData.heure_debut}
                onChange={(e) => setFormData({ ...formData, heure_debut: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lieu</label>
              <input
                type="text"
                value={formData.lieu}
                onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Durée (min)</label>
              <input
                type="number"
                min="15"
                step="15"
                value={formData.duree_prevue_min}
                onChange={(e) => setFormData({ ...formData, duree_prevue_min: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
              <select
                value={formData.statut}
                onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 bg-white"
              >
                <option value="Planifiée">Planifiée</option>
                <option value="En cours">En cours</option>
                <option value="Complétée">Complétée</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Objectif principal</label>
              <textarea
                rows={3}
                value={formData.objectif_principal}
                onChange={(e) => setFormData({ ...formData, objectif_principal: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Nom de la réunion</div>
              <div className="font-semibold text-gray-800">{reunion.type_rencontre}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Date</div>
              <div className="font-semibold text-gray-800">
                {formatDateLocal(reunion.date_reunion, 'fr-CA', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Heure / Durée</div>
              <div className="font-semibold text-gray-800">
                {(reunion.heure_debut || '--:--')}{' '}
                <span className="text-gray-500 font-medium">({reunion.duree_prevue_min || 60} min)</span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Lieu</div>
              <div className="font-semibold text-gray-800">{reunion.lieu || '—'}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 md:col-span-2">
              <div className="text-sm text-gray-500 mb-1">Statut</div>
              <div className="font-semibold text-gray-800">{reunion.statut || 'Planifiée'}</div>
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Users className="w-7 h-7 text-blue-600" />
            Participants ({reunion.participants?.length || 0})
          </h3>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Ajouter
          </button>
        </div>

        {showAdd && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-6 mb-6">
            <h4 className="font-bold text-gray-800 mb-4">Nouveau Participant</h4>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Sélectionner depuis les contacts</label>
              <select
                value={selectedPP}
                onChange={(e) => handleSelectPP(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">-- Saisie manuelle --</option>
                {partiesPrenantes.map((pp) => (
                  <option key={pp.id} value={String(pp.id)}>
                    {pp.prenom} {pp.nom} {pp.titre_poste ? `- ${pp.titre_poste}` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-blue-700 mt-2">
                Sélectionnez un contact BZ existant ou laissez vide pour saisie manuelle
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                <input
                  type="text"
                  value={newParticipant.nom || ''}
                  onChange={(e) => setNewParticipant({ ...newParticipant, nom: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rôle</label>
                <select
                  value={newParticipant.role || 'Membre'}
                  onChange={(e) => setNewParticipant({ ...newParticipant, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="Président">Président</option>
                  <option value="Vice-président">Vice-président</option>
                  <option value="Secrétaire">Secrétaire</option>
                  <option value="Membre">Membre</option>
                  <option value="Observateur">Observateur</option>
                  <option value="Invité">Invité</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={newParticipant.email || ''}
                  onChange={(e) => setNewParticipant({ ...newParticipant, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 px-4 py-3 bg-white border border-blue-200 rounded-lg w-full">
                  <input
                    type="checkbox"
                    checked={!!newParticipant.present}
                    onChange={(e) => setNewParticipant({ ...newParticipant, present: e.target.checked })}
                  />
                  <span className="text-sm text-gray-700 font-medium">Présent</span>
                </label>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4">
              <button onClick={addOneParticipant} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Ajouter
              </button>
              <button onClick={() => setShowAdd(false)} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                Annuler
              </button>
            </div>
          </div>
        )}

        {reunion.participants && reunion.participants.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reunion.participants.map((p) => (
              <div key={p.id} className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h4 className="font-bold text-gray-800">{p.nom}</h4>
                    <p className="text-sm text-blue-600 font-medium">{p.role}</p>
                    {p.email && <p className="text-sm text-gray-500">{p.email}</p>}
                  </div>
                  {p.id && (
                    <button
                      onClick={() => removeParticipant(p.id!, p.nom)}
                      className="p-2 rounded-lg hover:bg-red-100 text-red-600"
                      title="Retirer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {p.id && (
                  <div className="flex items-center gap-3 pt-3 border-t border-gray-200 mt-3">
                    <span className="text-sm text-gray-600 flex-1">Présence</span>
                    <button
                      onClick={() => togglePresence(p.id!, p.present)}
                      className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${
                        p.present ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block w-4 h-4 transform transition-transform bg-white rounded-full ${
                          p.present ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className={`text-xs font-bold ${p.present ? 'text-green-700' : 'text-gray-600'}`}>
                      {p.present ? '✓ Présent' : 'Absent'}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Aucun participant pour le moment</p>
          </div>
        )}
      </div>
    </div>
  );
}

